import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authService } from '@/services';
import type { User, BaseComponentProps } from '@/types';

interface AuthGuardProps extends BaseComponentProps {
  fallback?: string;
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  fallback = '/login', 
  children 
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        // Check for existing session
        const session = await authService.getSession();
        
        if (isMounted) {
          if (session?.user) {
            setUser(session.user);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (isMounted) {
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Listen to auth state changes
    const unsubscribe = authService.onAuthStateChange((authState) => {
      if (isMounted) {
        setUser(authState.user);
        setIsAuthenticated(authState.isAuthenticated);
        setIsLoading(authState.isLoading);
      }
    });

    checkAuth();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={fallback} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default AuthGuard;